// packages/shared-components/src/components/Keyboard/gapConfig.ts

/**
 * 键盘间距规则配置
 * 用于动态插入按键之间的间距
 */

export interface GapRule {
  /** 行索引 */
  row: number;
  /** 在该索引后插入间距 */
  afterIndex: number;
  /** 间距大小（单位：u） */
  size: number;
}
/** 63% 键盘无额外间距 */
export const LAYOUT_63_GAP_RULES: GapRule[] = [];

/** 65% 键盘无额外间距 */
export const LAYOUT_68_GAP_RULES: GapRule[] = [];

/** 80% TKL 键盘间距规则 */
export const LAYOUT_87_GAP_RULES: GapRule[] = [
  // Row 0: F键行
  { row: 0, afterIndex: 1, size: 1.1 }, // Esc 后（索引1是空键）
  { row: 0, afterIndex: 5, size: 0.525 }, // F4 后
  { row: 0, afterIndex: 9, size: 0.525 }, // F8 后
  { row: 0, afterIndex: 13, size: 0.33 }, // F12 后

  // Row 1: 数字行
  { row: 1, afterIndex: 13, size: 0.25 }, // Backspace 后
  // { row: 1, afterIndex: 16, size: 0.25 }, // PgUp 后

  // Row 2: Tab行
  { row: 2, afterIndex: 13, size: 0.25 }, // Backslash 后
  // { row: 2, afterIndex: 16, size: 0.25 }, // PgDn 后
  // Row 3: Caps行
  // { row: 3, afterIndex: 13, size: 4.285 }, // Enter 后

  // Row 4: Shift行
  { row: 4, afterIndex: 12, size: 1.3 }, // RightShift 后
  { row: 4, afterIndex: 16, size: 1.1 }, // ↑ 后

  // Row 5: 底部行
  { row: 5, afterIndex: 13, size: 0.25 }, // CtrlRight 后
  // { row: 5, afterIndex: 16, size: 0.3 },  // → 后
];

/** 智慧无限 DK87（6×17，Esc 与 F1 间留空列） */
export const LAYOUT_DK87_GAP_RULES: GapRule[] = [
  { row: 0, afterIndex: 0, size: 1.1 },
  { row: 0, afterIndex: 5, size: 0.525 },
  { row: 0, afterIndex: 9, size: 0.525 },
  { row: 0, afterIndex: 13, size: 0.2 },
  { row: 1, afterIndex: 13, size: 0.15 },
  { row: 2, afterIndex: 13, size: 0.15 },
  { row: 4, afterIndex: 14, size: 0.2 },
  { row: 5, afterIndex: 13, size: 0.15 },
];

export const LAYOUT_HUAFENDA87_GAP_RULES: GapRule[] = [
  // Row 0: F键行
  { row: 0, afterIndex: 0, size: 1.1 }, // Esc 后（索引1是空键）
  { row: 0, afterIndex: 4, size: 0.525 }, // F4 后
  { row: 0, afterIndex: 8, size: 0.525 }, // F8 后
  { row: 0, afterIndex: 12, size: 0.33 }, // F12 后

  // Row 1: 数字行
  { row: 1, afterIndex: 13, size: 0.25 }, // Backspace 后
  // { row: 1, afterIndex: 16, size: 0.25 }, // PgUp 后

  // Row 2: Tab行
  { row: 2, afterIndex: 13, size: 0.25 }, // Backslash 后
  // { row: 2, afterIndex: 16, size: 0.25 }, // PgDn 后
  // Row 3: Caps行
  // { row: 3, afterIndex: 13, size: 4.285 }, // Enter 后

  // Row 4: Shift行
  { row: 4, afterIndex: 12, size: 1.3 }, // RightShift 后
  { row: 4, afterIndex: 16, size: 1.1 }, // ↑ 后

  // Row 5: 底部行
  { row: 5, afterIndex: 13, size: 0.25 }, // CtrlRight 后
  // { row: 5, afterIndex: 16, size: 0.3 },  // → 后
];

/** X75GC（华丰达）— PrtSc 与 Home 紧贴；右 Shift 与 ↑ 紧贴，不加额外缝 */
export const LAYOUT_X75GC_GAP_RULES: GapRule[] = [
  { row: 0, afterIndex: 0, size: 0.25 },
  { row: 0, afterIndex: 5, size: 0.25 },
  { row: 0, afterIndex: 9, size: 0.25 },
  { row: 0, afterIndex: 13, size: 0.35 },
  { row: 5, afterIndex: 10, size: 0.5 },
];

export const LAYOUT_X75V2_GAP_RULES: GapRule[] = [
  // Row 0: F键行 + Ins + 旋钮
  { row: 0, afterIndex: 0, size: 0.25 }, // Esc 后（索引1是空键）
  { row: 0, afterIndex: 5, size: 0.25 }, // F4 后
  { row: 0, afterIndex: 9, size: 0.25 }, // F8 后
  { row: 0, afterIndex: 13, size: 0.35 }, // F12 后
  { row: 0, afterIndex: 14, size: 0.2 }, // F 行右侧功能键后

  // Row 1: 数字行
  { row: 1, afterIndex: 13, size: 0.2 }, // Backspace 后

  // Row 2: Tab行
  { row: 2, afterIndex: 13, size: 0.2 }, // Backslash 后

  // Row 3: Caps行
  { row: 3, afterIndex: 12, size: 0.2 }, // Enter 后

  // Row 4: Shift行
  { row: 4, afterIndex: 11, size: 0.15 }, // RightShift 后
  { row: 4, afterIndex: 12, size: 0.2 }, // ↑ 后

  // Row 5: 底部行（R-Ctrl 与 ← 之间加宽，对齐实机）
  { row: 5, afterIndex: 12, size: 0.74 },
];

/** M75（贝盈 916）— 75% 布局，Pause + 旋钮 + 右侧 Ins/Del/PgUp/PgDn 导航列 */
export const LAYOUT_M75_GAP_RULES: GapRule[] = [
  // Row 0: F键行 + Pause + 旋钮
  { row: 0, afterIndex: 0, size: 0.25 }, // Esc 后（索引1是空键）
  { row: 0, afterIndex: 5, size: 0.25 }, // F4 后
  { row: 0, afterIndex: 9, size: 0.25 }, // F8 后
  { row: 0, afterIndex: 13, size: 0.35 }, // F12 后
  { row: 0, afterIndex: 14, size: 0.2 }, // Pause 后（旋钮前）

  // Row 1: 数字行
  { row: 1, afterIndex: 13, size: 0.2 }, // Backspace 后

  // Row 2: Tab行
  { row: 2, afterIndex: 13, size: 0.2 }, // Backslash 后

  // Row 3: Caps行
  { row: 3, afterIndex: 12, size: 0.2 }, // Enter 后

  // Row 4: Shift行 + ↑ + PgDn
  { row: 4, afterIndex: 11, size: 0.15 }, // RightShift 后
  { row: 4, afterIndex: 12, size: 0.2 }, // ↑ 后
  { row: 4, afterIndex: 13, size: 0.2 }, // PgDn 后

  // Row 5: 底部行
  { row: 5, afterIndex: 12, size: 0.17 }, // CtrlRight 后
];

/** ES87 TKL：Esc 与 F1 无空键列；F 行含独立 F13(104) */
export const LAYOUT_ES87_GAP_RULES: GapRule[] = [
  // F 区分组缝略缩小；四段 gap 合计约 1.15u，使 F13 右缘与 Backspace 右缘对齐
  { row: 0, afterIndex: 0, size: 0.25 }, // Esc 后
  { row: 0, afterIndex: 4, size: 0.25 }, // F4 后
  { row: 0, afterIndex: 8, size: 0.25 }, // F8 后
  { row: 0, afterIndex: 12, size: 0.4 }, // F12 后（至 F13）
  { row: 0, afterIndex: 13, size: 0.2 }, // F13 后

  { row: 1, afterIndex: 13, size: 0.25 },
  { row: 2, afterIndex: 13, size: 0.25 },
  // Shift 行与标准 87 一致：gap 在 R-Shift 后第一格占位列之后，而非紧跟 R-Shift
  { row: 4, afterIndex: 12, size: 1.3 }, // 占位列后（R-Shift 与 ↑ 之间）
  { row: 4, afterIndex: 16, size: 1.1 }, // ↑ 后
  { row: 5, afterIndex: 13, size: 0.25 },
];

/**
 * X75 紧凑 75% 配列（Gamma75 / X75 V3 等同构）：
 * 底行方向键与 RCtrl 之间不额外加宽，与其它键一致仅用标准 keyGap。
 */
export const LAYOUT_X75_COMPACT_GAP_RULES: GapRule[] = [];

/** @deprecated 使用 LAYOUT_X75_COMPACT_GAP_RULES */
export const LAYOUT_X75V3_GAP_RULES = LAYOUT_X75_COMPACT_GAP_RULES;

/** @deprecated 使用 LAYOUT_X75_COMPACT_GAP_RULES */
export const LAYOUT_GAMMA75_GAP_RULES = LAYOUT_X75_COMPACT_GAP_RULES;

/** DK75 / ZE75：F 行 Esc/F4/F8/F12 后 0.2u 间隔；底行 Fn/↓ 与方向键间各 0.2u */
export const LAYOUT_DK75_GAP_RULES: GapRule[] = [
  { row: 0, afterIndex: 0, size: 0.2 },
  { row: 0, afterIndex: 4, size: 0.2 },
  { row: 0, afterIndex: 8, size: 0.2 },
  { row: 0, afterIndex: 12, size: 0.45 },
  { row: 4, afterIndex: 11, size: 0.15 },
  { row: 5, afterIndex: 11, size: 0.2 },
];

/**
 * 智慧无限 DK75 Single（17 列 75% 紧凑）：Esc 与 F1 同排无空列，F 组 / 导航列 / 底行按公版视觉校准。
 */
export const LAYOUT_DK75_SINGLE_GAP_RULES: GapRule[] = [
  { row: 0, afterIndex: 0, size: 1.1 },
  { row: 0, afterIndex: 4, size: 0.5 },
  { row: 0, afterIndex: 8, size: 0.5 },
  { row: 0, afterIndex: 12, size: 0.2 },
  { row: 1, afterIndex: 13, size: 0.2 },
  { row: 2, afterIndex: 13, size: 0.2 },
  { row: 3, afterIndex: 12, size: 0.2 },
  { row: 4, afterIndex: 12, size: 0.15 },
  { row: 4, afterIndex: 13, size: 0.15 },
  { row: 5, afterIndex: 12, size: 0.3 },
];

export const LAYOUT_75PRO_GAP_RULES: GapRule[] = [
  // Row 0: F键行
  { row: 0, afterIndex: 0, size: 1.1 }, // Esc 后（索引1是空键）
  { row: 0, afterIndex: 5, size: 0.525 }, // F4 后
  { row: 0, afterIndex: 9, size: 0.525 }, // F8 后
  { row: 0, afterIndex: 13, size: 0.2 }, // F12 后

  // Row 1: 数字行
  { row: 1, afterIndex: 13, size: 0.2 }, // Backspace 后

  // Row 2: Tab行
  { row: 2, afterIndex: 13, size: 0.2 }, // Backslash 后

  // Row 3: Caps行
  { row: 3, afterIndex: 12, size: 0.2 }, // Enter 后

  // Row 4: Shift行
  { row: 4, afterIndex: 11, size: 0.15 }, // RightShift 后
  { row: 4, afterIndex: 12, size: 0.2 }, // ↑ 后

  // Row 5: 底部行
  { row: 5, afterIndex: 12, size: 0.17 }, // CtrlRight 后
  // { row: 5, afterIndex: 16, size: 0.3 },  // → 后
];

export const LAYOUT_M98_GAP_RULES: GapRule[] = [
  // Row 0: F键行
  { row: 0, afterIndex: 0, size: 0.2 }, // Esc 后
  { row: 0, afterIndex: 4, size: 0.2 }, // F4 后
  { row: 0, afterIndex: 8, size: 0.2 }, // F8 后
  { row: 0, afterIndex: 12, size: 0.2 }, // F12 后
  { row: 0, afterIndex: 13, size: 0.25 }, // Del 后

  // Row 1: 数字行
  { row: 1, afterIndex: 13, size: 0.25 }, // Backspace 后

  // Row 2: Tab行
  { row: 2, afterIndex: 13, size: 0.25 }, // Backslash 后

  // Row 3: Caps行
  { row: 3, afterIndex: 12, size: 0.25 }, // Enter 后

  // Row 4: Shift行
  { row: 4, afterIndex: 11, size: 0.1 }, // RightShift 后
  { row: 4, afterIndex: 12, size: 0.1 }, // ↑ 后

  // Row 5: 底部行
  { row: 5, afterIndex: 12, size: 0.08 }, // CtrlRight 后
  { row: 5, afterIndex: 15, size: 0.08 }, // → 后
];

export const LAYOUT_M98TFT_GAP_RULES: GapRule[] = [
  // Row 0: F键行
  { row: 0, afterIndex: 0, size: 0.2 }, // Esc 后
  { row: 0, afterIndex: 4, size: 0.2 }, // F4 后
  { row: 0, afterIndex: 8, size: 0.2 }, // F8 后
  { row: 0, afterIndex: 12, size: 0.2 }, // F12 后
  { row: 0, afterIndex: 13, size: 0.45 }, // Del 后

  // Row 1: 数字行
  { row: 1, afterIndex: 13, size: 0.25 }, // Backspace 后

  // Row 2: Tab行
  { row: 2, afterIndex: 13, size: 0.25 }, // Backslash 后

  // Row 3: Caps行
  { row: 3, afterIndex: 12, size: 0.25 }, // Enter 后

  // Row 4: Shift行
  { row: 4, afterIndex: 11, size: 0.1 }, // RightShift 后
  { row: 4, afterIndex: 12, size: 0.1 }, // ↑ 后

  // Row 5: 底部行
  { row: 5, afterIndex: 12, size: 0.08 }, // CtrlRight 后
  { row: 5, afterIndex: 15, size: 0.08 }, // → 后
];

export const LAYOUT_M98_8K_GAP_RULES: GapRule[] = [
  // Row 0: F键行
  { row: 0, afterIndex: 0, size: 0.2 }, // Esc 后
  { row: 0, afterIndex: 4, size: 0.2 }, // F4 后
  { row: 0, afterIndex: 8, size: 0.2 }, // F8 后
  { row: 0, afterIndex: 12, size: 0.2 }, // F12 后
  { row: 0, afterIndex: 13, size: 0.45 }, // Del 后

  // Row 1: 数字行
  { row: 1, afterIndex: 13, size: 0.25 }, // Backspace 后

  // Row 2: Tab行
  { row: 2, afterIndex: 13, size: 0.25 }, // Backslash 后

  // Row 3: Caps行
  { row: 3, afterIndex: 12, size: 0.25 }, // Enter 后

  // Row 4: Shift行
  { row: 4, afterIndex: 11, size: 0.1 }, // RightShift 后
  { row: 4, afterIndex: 12, size: 0.1 }, // ↑ 后

  // Row 5: 底部行
  { row: 5, afterIndex: 12, size: 0.08 }, // CtrlRight 后
  { row: 5, afterIndex: 15, size: 0.08 }, // → 后
];

export const LAYOUT_75PROTFT_GAP_RULES: GapRule[] = [
  // Row 0: F键行
  { row: 0, afterIndex: 0, size: 1.1 }, // Esc 后（索引1是空键）
  { row: 0, afterIndex: 5, size: 0.525 }, // F4 后
  { row: 0, afterIndex: 9, size: 0.525 }, // F8 后
  { row: 0, afterIndex: 13, size: 0.2 }, // F12 后

  // Row 1: 数字行
  { row: 1, afterIndex: 13, size: 0.2 }, // Backspace 后

  // Row 2: Tab行
  { row: 2, afterIndex: 13, size: 0.2 }, // Backslash 后

  // Row 3: Caps行
  { row: 3, afterIndex: 12, size: 0.2 }, // Enter 后

  // Row 4: Shift行
  { row: 4, afterIndex: 11, size: 0.15 }, // RightShift 后
  { row: 4, afterIndex: 12, size: 0.2 }, // ↑ 后

  // Row 5: 底部行
  { row: 5, afterIndex: 12, size: 0.17 }, // CtrlRight 后
  // { row: 5, afterIndex: 16, size: 0.3 },  // → 后
];
/** 80% TKL 键盘间距规则（SMART875） */
export const LAYOUT_84_GAP_RULES: GapRule[] = [
  // Row 0: F键行
  { row: 0, afterIndex: 1, size: 0.25 }, // Esc 后（索引1是空键）
  { row: 0, afterIndex: 5, size: 0.25 }, // F4 后
  { row: 0, afterIndex: 9, size: 0.25 }, // F8 后
  { row: 0, afterIndex: 13, size: 0.38 }, // F12 后
  { row: 0, afterIndex: 14, size: 0.25 }, // F12 后

  // Row 1: 数字行
  { row: 1, afterIndex: 13, size: 0.25 }, // Backspace 后
  // { row: 1, afterIndex: 16, size: 0.25 }, // PgUp 后

  // Row 2: Tab行
  { row: 2, afterIndex: 13, size: 0.25 }, // Backslash 后
  // { row: 2, afterIndex: 16, size: 0.25 }, // PgDn 后
  // Row 3: Caps行
  // { row: 3, afterIndex: 13, size: 4.285 }, // Enter 后

  // Row 4: Shift行
  { row: 4, afterIndex: 12, size: 0.25 }, // RightShift 后
  { row: 4, afterIndex: 16, size: 1.1 }, // ↑ 后

  // Row 5: 底部行
  { row: 5, afterIndex: 12, size: 0.25 }, // CtrlRight 后
  // { row: 5, afterIndex: 16, size: 0.3 },  // → 后
];
/** DEEP80 MAX：间距规则与 SMART875(84) 一致 */
export const LAYOUT_DEEP80MAX_GAP_RULES: GapRule[] = [
  // Row 0: F键行
  { row: 0, afterIndex: 0, size: 0.2875 }, // Esc 后（索引1是空键）
  { row: 0, afterIndex: 4, size: 0.2875 }, // F4 后
  { row: 0, afterIndex: 8, size: 0.2875 }, // F8 后
  { row: 0, afterIndex: 12, size: 0.2875 }, // F8 后
  { row: 0, afterIndex: 13, size: 0.25 }, // F12 后
  // { row: 0, afterIndex: 14, size: 0.25 }, // F12 后

  // Row 1: 数字行
  { row: 1, afterIndex: 13, size: 0.25 }, // Backspace 后
  // { row: 1, afterIndex: 16, size: 0.25 }, // PgUp 后

  // Row 2: Tab行
  { row: 2, afterIndex: 13, size: 0.25 }, // Backslash 后
  // { row: 2, afterIndex: 16, size: 0.25 }, // PgDn 后
  // Row 3: Caps行
  // { row: 3, afterIndex: 13, size: 4.285 }, // Enter 后

  // Row 4: Shift行
  { row: 4, afterIndex: 13, size: 0.26 }, // RightShift 后
  { row: 4, afterIndex: 16, size: 1.1 }, // ↑ 后

  // Row 5: 底部行
  { row: 5, afterIndex: 12, size: 0.25 }, // CtrlRight 后
  // { row: 5, afterIndex: 16, size: 0.3 },  // → 后
];

/** 80% TKL 键盘间距规则 */
export const LAYOUT_99_GAP_RULES: GapRule[] = [
  // Row 0: F键行
  { row: 0, afterIndex: 1, size: 0.25 }, // Esc 后（索引1是空键）
  { row: 0, afterIndex: 5, size: 0.25 }, // F4 后
  { row: 0, afterIndex: 9, size: 0.25 }, // F8 后
  { row: 0, afterIndex: 13, size: 0.38 }, // F12 后
  { row: 0, afterIndex: 14, size: 0.25 }, // F12 后

  // Row 1: 数字行
  { row: 1, afterIndex: 13, size: 0.25 }, // Backspace 后
  // { row: 1, afterIndex: 16, size: 0.25 }, // PgUp 后

  // Row 2: Tab行
  { row: 2, afterIndex: 13, size: 0.25 }, // Backslash 后
  // { row: 2, afterIndex: 16, size: 0.25 }, // PgDn 后
  // Row 3: Caps行
  { row: 3, afterIndex: 12, size: 0.25 }, // Enter 后

  // Row 4: Shift行
  { row: 4, afterIndex: 15, size: 0.26 }, // RightShift 后
  // { row: 4, afterIndex: 16, size: 1.1 }, // ↑ 后

  // Row 5: 底部行
  // { row: 5, afterIndex: 12, size: 0.25 }, // CtrlRight 后
  { row: 5, afterIndex: 15, size: 0.15 }, // → 后
];

/** M80 / M71 机甲：首行 Esc 与 F1 无空键列，与 98 规则略有不同 */
export const LAYOUT_M80_GAP_RULES: GapRule[] = [
  { row: 0, afterIndex: 0, size: 0.55 }, // Esc 前
];

export const LAYOUT_M71_GAP_RULES: GapRule[] = [
  { row: 1, afterIndex: 13, size: 0.3 }, // Insert 前
  { row: 2, afterIndex: 13, size: 0.3 }, // End 前
  { row: 3, afterIndex: 12, size: 0.3 }, // Delete 前
  { row: 4, afterIndex: 11, size: 0.3 }, // 上箭头 前
  // 底行无 21252 占位后，R-Ctrl 在 col12、← 在 col13；间距仍在 ← 前（即 after R-Ctrl）
  { row: 5, afterIndex: 13, size: 0.3 },
];

export const LAYOUT_T87_GAP_RULES: GapRule[] = [
  { row: 0, afterIndex: 0, size: 1.1 },
  { row: 0, afterIndex: 4, size: 0.525 },
  { row: 0, afterIndex: 8, size: 0.525 },
  { row: 0, afterIndex: 12, size: 0.33 },

  // Row 1: 数字行
  { row: 1, afterIndex: 13, size: 0.25 },

  // Row 2: Tab行
  { row: 2, afterIndex: 13, size: 0.25 },

  // Row 4: Shift 行（RShift 与 ↑ 之间留空）
  { row: 4, afterIndex: 12, size: 1.3 },

  // Row 5: 底部行
  { row: 5, afterIndex: 13, size: 0.25 },
];

/** 80% TKL 键盘间距规则 */
export const LAYOUT_98_GAP_RULES: GapRule[] = [
  // Row 0: F键行
  { row: 0, afterIndex: 1, size: 1.1 }, // Esc 后（索引1是空键）
  { row: 0, afterIndex: 5, size: 0.525 }, // F4 后
  { row: 0, afterIndex: 9, size: 0.525 }, // F8 后
  { row: 0, afterIndex: 13, size: 0.15 }, // F12 后
  { row: 0, afterIndex: 14, size: 0.15 }, // Del 后

  // Row 1: 数字行
  { row: 1, afterIndex: 13, size: 0.15 }, // Backspace 后
  { row: 1, afterIndex: 14, size: 0.15 }, // Insert 后

  // Row 2: Tab行
  { row: 2, afterIndex: 13, size: 0.15 }, // \键 后
  { row: 2, afterIndex: 14, size: 0.15 }, // PgUp 后

  // Row 3: Caps行
  { row: 3, afterIndex: 13, size: 0.15 }, // Enter 后
  { row: 3, afterIndex: 14, size: 0.15 }, // PgDn 后

  // Row 4: Shift行
  { row: 4, afterIndex: 13, size: 1.35 }, // ↑ 后

  // Row 5: 底部行
  { row: 5, afterIndex: 11, size: 0.6 }, // Fn 后
  { row: 5, afterIndex: 14, size: 0.3 }, // → 后
];

/**
 * V102 间距
 * - 主区→导航列统一 0.3（Ins/PgUp/PgDn/Del 左缘 15.6）
 * - ↑ 紧挨 R-Shift（14.6–15.6），再与小键盘留白
 * - ←↓→：↓ 对准 ↑，→ 对准导航列，不侵入小键盘
 */
export const LAYOUT_V102_GAP_RULES: GapRule[] = [
  { row: 0, afterIndex: 1, size: 1.1 },
  { row: 0, afterIndex: 5, size: 0.525 },
  { row: 0, afterIndex: 9, size: 0.525 },
  { row: 0, afterIndex: 13, size: 0.45 },
  { row: 0, afterIndex: 14, size: 0.45 },
  { row: 1, afterIndex: 13, size: 0.45 },
  { row: 1, afterIndex: 14, size: 0.15 },
  { row: 2, afterIndex: 13, size: 0.45 },
  { row: 2, afterIndex: 14, size: 0.15 },
  { row: 3, afterIndex: 12, size: 0.45 },
  { row: 3, afterIndex: 13, size: 0.15 },
  // ↑ 后到小键盘（15.6 → 16.75）
  { row: 4, afterIndex: 12, size: 1.25 },
  // R-Ctrl → ←（← 在 13.6，↓ 对准 ↑）
  { row: 5, afterIndex: 12, size: 0.6 },
  // → 后到 P0
  { row: 5, afterIndex: 15, size: 0.15 },
];

/** 100% 键盘间距规则 */
export const LAYOUT_104_GAP_RULES: GapRule[] = [
  // Row 0: F键行
  { row: 0, afterIndex: 1, size: 1.1 }, // Esc 后（索引1是空键）
  { row: 0, afterIndex: 5, size: 0.55 }, // F4 后
  { row: 0, afterIndex: 9, size: 0.55 }, // F8 后
  { row: 0, afterIndex: 13, size: 0.55 }, // F12 后

  // Row 1: 数字行
  { row: 1, afterIndex: 13, size: 0.5 }, // Backspace 后
  { row: 1, afterIndex: 16, size: 0.5 }, // PgUp 后

  // Row 2: Tab行
  { row: 2, afterIndex: 13, size: 0.5 }, // Backslash 后
  { row: 2, afterIndex: 16, size: 0.5 }, // PgDn 后
  // Row 3: Caps行
  { row: 3, afterIndex: 13, size: 4.3 }, // Enter 后

  // Row 4: Shift行
  { row: 4, afterIndex: 12, size: 1.48 }, // RightShift 后
  { row: 4, afterIndex: 16, size: 1.75 }, // ↑ 后

  // Row 5: 底部行
  { row: 5, afterIndex: 13, size: 0.5 }, // CtrlRight 后
  { row: 5, afterIndex: 16, size: 0.61 }, // → 后
];

/** DK108：108 键全尺寸间距（独立维护，初版与 104 一致） */
export const LAYOUT_DK108_GAP_RULES: GapRule[] = [
  { row: 0, afterIndex: 1, size: 1.1 },
  { row: 0, afterIndex: 5, size: 0.55 },
  { row: 0, afterIndex: 9, size: 0.55 },
  { row: 0, afterIndex: 13, size: 0.25 },
  { row: 0, afterIndex: 16, size: 0.25 },
  { row: 1, afterIndex: 13, size: 0.25 },
  { row: 1, afterIndex: 16, size: 0.25 },
  { row: 2, afterIndex: 13, size: 0.25 },
  { row: 2, afterIndex: 16, size: 0.25 },
  { row: 3, afterIndex: 13, size: 3.75 },
  { row: 4, afterIndex: 12, size: 1.35 },
  { row: 4, afterIndex: 16, size: 1.33 },
  { row: 5, afterIndex: 13, size: 0.25 },
  { row: 5, afterIndex: 16, size: 0.25 },
];

/**
 * 根据键盘类型获取间距规则
 */
export const getGapRules = (keyboardType: string): GapRule[] => {
  switch (keyboardType) {
    case "104":
    case "keymap104":
      return LAYOUT_104_GAP_RULES;
    case "DK108":
      return LAYOUT_DK108_GAP_RULES;
    case "87":
      return LAYOUT_87_GAP_RULES;
    case "es87":
      return LAYOUT_ES87_GAP_RULES;
    case "huafenda87":
    case "rongyuan87":
      return LAYOUT_HUAFENDA87_GAP_RULES;
    case "84":
      return LAYOUT_84_GAP_RULES;
    case "deep80max":
      return LAYOUT_DEEP80MAX_GAP_RULES;
    case "99":
      return LAYOUT_99_GAP_RULES;
    case "98":
    case "huafenda98":
      return LAYOUT_98_GAP_RULES;
    case "v102":
      return LAYOUT_V102_GAP_RULES;
    case "X98_1K":
      return LAYOUT_98_GAP_RULES;
    case "m80":
      return LAYOUT_M80_GAP_RULES;
    case "m71":
      return LAYOUT_M71_GAP_RULES;
    case "t87":
      return LAYOUT_T87_GAP_RULES;
    case "x75v2":
      return LAYOUT_X75V2_GAP_RULES;
    case "m75":
      return LAYOUT_M75_GAP_RULES;
    case "x75gc":
      return LAYOUT_X75GC_GAP_RULES;
    case "63":
    case "dk63Machine":
      return LAYOUT_63_GAP_RULES;
    case "75Pro":
      return LAYOUT_75PRO_GAP_RULES;
    case "x75v3":
    case "gamma75":
      return LAYOUT_X75_COMPACT_GAP_RULES;
    case "dk75":
    case "dk75Pro":
    case "dk75E":
      return LAYOUT_DK75_GAP_RULES;
    case "dk87":
      return LAYOUT_DK87_GAP_RULES;
    case "dk75Single":
      return LAYOUT_DK75_SINGLE_GAP_RULES;
    case "M98":
      return LAYOUT_M98_GAP_RULES;
    case "75ProTFT":
      return LAYOUT_75PROTFT_GAP_RULES;
    case "M98TFT":
      return LAYOUT_M98TFT_GAP_RULES;
    case "M98 8K":
      return LAYOUT_M98_8K_GAP_RULES;
    case "68":
    case "huafenda68":
    case "tank68he":
    case "68EVO":
    default:
      return LAYOUT_68_GAP_RULES;
  }
};

/**
 * 转换为查询 Map（性能优化）
 */
export const getGapRulesMap = (
  keyboardType: string
): Record<number, Record<number, number>> => {
  const rules = getGapRules(keyboardType);
  const map: Record<number, Record<number, number>> = {};

  rules.forEach(({ row, afterIndex, size }) => {
    if (!map[row]) map[row] = {};
    map[row][afterIndex] = size;
  });

  return map;
};
